<template>
  <IntercomLayout>
    <template #topbar>
      <SevaTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="expenses-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('expense.title') }}</h1>
          <p class="page-subtitle">{{ $t('expense.subtitle') }}</p>
        </div>
        <div class="header-actions">
          <IntercomButton variant="secondary" @click="showCategoryModal = true">
            {{ $t('expense.manageCategories') }}
          </IntercomButton>
          <IntercomButton variant="primary" @click="showAddModal = true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{{ $t('expense.addExpense') }}</span>
          </IntercomButton>
        </div>
      </div>

      <!-- Filters -->
      <div class="filters-row">
        <IntercomInput
          v-model="searchQuery"
          :placeholder="$t('search.expensesPlaceholder')"
          type="search"
          class="search-input"
          @input="handleSearch"
        />
        <IntercomSelect
          v-model="filterCategory"
          :options="categoryFilterOptions"
          :placeholder="$t('filters.allCategories')"
          class="filter-select"
          filterable
          @update:model-value="fetchExpenses"
        />
        <IntercomDateInput
          v-model="filterDateFrom"
          class="date-input"
          @update:model-value="fetchExpenses"
        />
        <span class="date-separator">to</span>
        <IntercomDateInput
          v-model="filterDateTo"
          class="date-input"
          @update:model-value="fetchExpenses"
        />
      </div>

      <!-- Expenses Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="expenses"
          :loading="isLoading"
          hoverable
          clickable
          @row-click="handleRowClick"
        >
          <template #cell-category="{ value }">
            <IntercomBadge variant="info">
              {{ value?.name }}
            </IntercomBadge>
          </template>
          <template #cell-amount="{ value }">
            {{ formatCompactCurrency(value || 0) }}
          </template>
          <template #cell-expense_date="{ value }">
            {{ formatDate(value) }}
          </template>
        </IntercomTable>
        <p v-if="!isLoading && expenses.length === 0" class="empty-text">
          {{ $t('expense.noExpenses') }}
        </p>
      </IntercomCard>

      <IntercomPagination
        :offset="offset"
        :total="total"
        :limit="limit"
        @update:offset="(v) => { offset.value = v; fetch() }"
      />

      <!-- Add/Edit Modal -->
      <IntercomModal v-model:show="showAddModal" :title="editingExpense ? $t('expense.editExpense') : $t('expense.addExpense')">
        <ExpenseForm
          :expense="editingExpense"
          :categories="categories"
          :programs="programs"
          :bank-accounts="bankAccounts"
          :loading="isSaving"
          @submit="handleSave"
          @cancel="closeModal"
        />
      </IntercomModal>

      <!-- Category Manager Modal -->
      <IntercomModal v-model:show="showCategoryModal" :title="$t('expense.manageCategories')">
        <CategoryManager
          :categories="categories"
          @create="handleCreateCategory"
          @update="handleUpdateCategory"
          @close="showCategoryModal = false"
        />
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { usePaginatedList } from '@shared/composables/usePaginatedList.js'
import { useSeva } from '../composables/useSeva.js'
import { formatCompactCurrency } from '@shared/utils/currency.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomInput,
  IntercomDateInput,
  IntercomSelect,
  IntercomModal,
  IntercomPagination,
} from '@shared/components/intercom'
import SevaTopbar from '../components/SevaTopbar.vue'
import ExpenseForm from '../components/ExpenseForm.vue'
import CategoryManager from '../components/CategoryManager.vue'
import { useNavigation } from '../composables/useNavigation.js'
import dayjs from 'dayjs'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { listExpenses, listExpenseCategories, createExpense, updateExpense, createExpenseCategory, updateExpenseCategory, listPrograms, listBankAccounts } = useSeva()
const { navSections } = useNavigation()

// State
const categories = ref([])
const programs = ref([])
const bankAccounts = ref([])
const isSaving = ref(false)
const searchQuery = ref('')
const filterCategory = ref(null)
const filterDateFrom = ref(dayjs().startOf('month').format('YYYY-MM-DD'))
const filterDateTo = ref(dayjs().format('YYYY-MM-DD'))
const showAddModal = ref(false)
const showCategoryModal = ref(false)
const editingExpense = ref(null)

// Paginated list
const {
  items: expenses,
  total,
  loading: isLoading,
  offset,
  limit,
  fetch,
  resetAndFetch,
} = usePaginatedList(
  (params) => listExpenses({
    ...params,
    ...(searchQuery.value ? { search: searchQuery.value } : {}),
    ...(filterCategory.value ? { category_id: filterCategory.value } : {}),
    ...(filterDateFrom.value ? { date_from: filterDateFrom.value } : {}),
    ...(filterDateTo.value ? { date_to: filterDateTo.value } : {}),
  }),
  { immediate: false }
)

const formatDate = (date) => date ? dayjs(date).format('DD MMM YYYY') : ''

const categoryFilterOptions = computed(() => [
  { value: null, label: t('filters.allCategories') },
  ...categories.value.map(c => ({ value: c.id, label: c.name })),
])

const columns = [
  { key: 'expense_number', title: t('expense.number'), width: '120px' },
  { key: 'expense_date', title: t('expense.date'), width: '120px' },
  { key: 'category', title: t('expense.category'), width: '140px' },
  { key: 'amount', title: t('expense.amount'), width: '120px', align: 'right' },
  { key: 'vendor_name', title: t('expense.vendor'), width: '150px' },
  { key: 'description', title: t('expense.description'), width: '200px' },
]

const fetchExpenses = () => resetAndFetch()

const fetchCategories = async () => {
  try {
    const response = await listExpenseCategories()
    // apiClient.get() unwraps response.data.data, so we get { categories: [...] } directly
    categories.value = response?.categories || []
  } catch (error) {
    console.error('Failed to fetch categories:', error)
  }
}

const fetchPrograms = async () => {
  try {
    const response = await listPrograms()
    programs.value = response?.programs || []
  } catch (error) {
    console.error('Failed to fetch programs:', error)
  }
}

const fetchBankAccounts = async () => {
  try {
    const response = await listBankAccounts()
    bankAccounts.value = response?.accounts || []
  } catch (error) {
    console.error('Failed to fetch bank accounts:', error)
  }
}

const handleSearch = () => {
  resetAndFetch()
}

const handleRowClick = (expense) => {
  router.push(`/expenses/${expense.id}`)
}

const handleSave = async (formData) => {
  isSaving.value = true
  try {
    if (editingExpense.value) {
      await updateExpense(editingExpense.value.id, formData)
      toast.success(t('success.expenseUpdated'))
    } else {
      await createExpense(formData)
      toast.success(t('success.expenseCreated'))
    }
    closeModal()
    fetchExpenses()
  } catch (error) {
    console.error('Failed to save expense:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const handleCreateCategory = async (data) => {
  try {
    await createExpenseCategory(data)
    toast.success(t('success.categoryCreated'))
    fetchCategories()
  } catch (error) {
    console.error('Failed to create category:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  }
}

const handleUpdateCategory = async (id, data) => {
  try {
    await updateExpenseCategory(id, data)
    toast.success(t('success.categoryUpdated'))
    fetchCategories()
  } catch (error) {
    console.error('Failed to update category:', error)
    toast.error(error.detail || error.message || t('errors.saveFailed'))
  }
}

const closeModal = () => {
  showAddModal.value = false
  editingExpense.value = null
}

const handleLogout = async () => {
  await logout()
  router.push('/login')
}

onMounted(() => {
  fetchBusiness()
  fetchCategories()
  fetch()
  fetchPrograms()
  fetchBankAccounts()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';

.expenses-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.header-actions {
  display: flex;
  gap: var(--space-3);
}

.filters-row {
  display: flex;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
  align-items: center;
}

.search-input {
  flex: 1;
  max-width: 300px;
}

.filter-select {
  width: 180px;
}

.date-input {
  width: 150px;
}

.date-separator {
  color: var(--color-gray-500);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}

@media (max-width: 768px) {
  .expenses-view {
    padding: var(--space-4);
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-4);
  }

  .header-actions {
    flex-direction: column;
    width: 100%;
  }

  .filters-row {
    flex-direction: column;
  }

  .search-input,
  .filter-select,
  .date-input {
    max-width: none;
    width: 100%;
  }
}
</style>
